/**
 * Organization / multi-tenant Cloud Function wrappers
 */
import type { State } from '../../hooks/useAppState'
import type { ApiResponse } from '../types'
import { loadCloudFunctionsHandler } from '../handlers'
import type { CreateOrganizationRequest, Organization } from '../../../../pure-js/types/types'
import type { OrgRole, OrgMemberRow, UpdateOrgMemberRequest } from '../../../../pure-js/types/OrganizationMembership'
import type {
  InviteUsersToOrganizationRequest,
  InviteUsersToOrganizationResult
} from '../../../../pure-js/types/OrgInvitation'
import type { OrgInvitation } from '../../../../pure-js/types/OrgInvitation'

type CfOrgPayload<T> = { success?: boolean; data?: T }

const unwrapOrgPayload = <T>(res: ApiResponse<CfOrgPayload<T>>): ApiResponse<T | undefined> => {
  if (!res.success) {
    return { data: undefined, success: false, error: res.error }
  }
  const inner = res.data
  if (!inner || inner.success === false) {
    return { data: undefined, success: false, error: res.error }
  }
  return { data: inner.data, success: true }
}

const unwrapOrgListPayload = <T>(res: ApiResponse<CfOrgPayload<T[]>>): ApiResponse<T[]> => {
  if (!res.success) {
    return { data: [], success: false, error: res.error }
  }
  const inner = res.data
  if (!inner || inner.success === false) {
    return { data: [], success: false, error: res.error }
  }
  return { data: inner.data ?? [], success: true }
}

export const createOrganization = async (
  state: State,
  req: CreateOrganizationRequest
): Promise<ApiResponse<Organization | undefined>> => {
  const cf = await loadCloudFunctionsHandler()
  const res = await cf.createOrganization(req, state)
  return unwrapOrgPayload(res)
}

export const updateOrganization = async (
  state: State,
  req: { organizationId: string; payload: Partial<Organization> }
): Promise<ApiResponse<Organization | undefined>> => {
  const cf = await loadCloudFunctionsHandler()
  const res = await cf.updateOrganization(req, state)
  return unwrapOrgPayload(res)
}

export const getOrganization = async (
  state: State,
  req: { organizationId?: string }
): Promise<ApiResponse<Organization | undefined>> => {
  const cf = await loadCloudFunctionsHandler()
  const res = await cf.getOrganization(req, state)
  return unwrapOrgPayload(res)
}

/** The directory of organizations. */
export const getOrganizationsDirectory = async (state: State): Promise<ApiResponse<Organization[]>> => {
  const cf = await loadCloudFunctionsHandler()
  const res = await cf.getOrganizations(state)
  return unwrapOrgListPayload<Organization>(res as ApiResponse<CfOrgPayload<Organization[]>>)
}

export const setActiveOrganization = async (
  state: State,
  req: { organizationId: string }
): Promise<ApiResponse<{ activeOrganizationId: string } | undefined>> => {
  const cf = await loadCloudFunctionsHandler()
  const res = await cf.setActiveOrganization(req, state)
  return unwrapOrgPayload<{ activeOrganizationId: string }>(
    res as ApiResponse<CfOrgPayload<{ activeOrganizationId: string }>>
  )
}

export const getOrgInvites = async (state: State, organizationId: string): Promise<ApiResponse<OrgInvitation[]>> => {
  const cf = await loadCloudFunctionsHandler()
  const res = await cf.getOrgInvites({ organizationId }, state)
  return unwrapOrgListPayload<OrgInvitation>(res as ApiResponse<CfOrgPayload<OrgInvitation[]>>)
}

export type { OrgMemberRow } from '../../../../pure-js/types/OrganizationMembership'

export const getOrgMembers = async (state: State, organizationId: string): Promise<ApiResponse<OrgMemberRow[]>> => {
  const cf = await loadCloudFunctionsHandler()
  const res = await cf.getOrgMembers({ organizationId }, state)
  return unwrapOrgListPayload<OrgMemberRow>(res as ApiResponse<CfOrgPayload<OrgMemberRow[]>>)
}

export const revokeOrgInvite = async (state: State, inviteId: string): Promise<ApiResponse<boolean>> => {
  const cf = await loadCloudFunctionsHandler()
  const res = await cf.revokeOrgInvite({ inviteId }, state)
  const inner = res.data
  if (!res.success || !inner?.success) {
    return { data: false, success: false, error: res.error }
  }
  return { data: true, success: true }
}

export const resendOrgInvite = async (
  state: State,
  inviteId: string
): Promise<ApiResponse<{ inviteId: string; expiresAt: string } | undefined>> => {
  const cf = await loadCloudFunctionsHandler()
  const res = await cf.resendOrgInvite({ inviteId }, state)
  return unwrapOrgPayload<{ inviteId: string; expiresAt: string }>(
    res as ApiResponse<CfOrgPayload<{ inviteId: string; expiresAt: string }>>
  )
}

export const addMemberToOrganization = async (
  state: State,
  req: { organizationId: string; userId: string; role: OrgRole }
): Promise<ApiResponse<unknown>> => {
  const cf = await loadCloudFunctionsHandler()
  return cf.addMember(req, state)
}

export const removeUserFromOrganization = async (
  state: State,
  req: { organizationId: string; userId: string }
): Promise<ApiResponse<unknown>> => {
  const cf = await loadCloudFunctionsHandler()
  return cf.removeUserFromOrganization(req, state)
}

export const updateOrgMember = async (state: State, req: UpdateOrgMemberRequest): Promise<ApiResponse<unknown>> => {
  const cf = await loadCloudFunctionsHandler()
  return cf.updateOrgMember(req, state)
}

export const inviteUserToOrganization = async (
  state: State,
  req: { organizationId: string; email: string; role: OrgRole }
): Promise<ApiResponse<{ inviteId: string } | undefined>> => {
  const cf = await loadCloudFunctionsHandler()
  const res = await cf.inviteUserToOrganization(
    { organizationId: req.organizationId, email: req.email, role: req.role },
    state
  )
  return unwrapOrgPayload<{ inviteId: string }>(res)
}

export const createOrgMemberByEmail = async (
  state: State,
  req: { organizationId: string; email: string; role: OrgRole }
): Promise<ApiResponse<{ userId: string } | undefined>> => {
  const cf = await loadCloudFunctionsHandler()
  const res = await cf.createOrgMemberByEmail(
    { organizationId: req.organizationId, email: req.email, role: req.role },
    state
  )
  return unwrapOrgPayload<{ userId: string }>(res)
}

export const inviteUsersToOrganization = async (
  state: State,
  req: InviteUsersToOrganizationRequest
): Promise<ApiResponse<InviteUsersToOrganizationResult | undefined>> => {
  const cf = await loadCloudFunctionsHandler()
  const res = await cf.inviteUsersToOrganization(req, state)
  return unwrapOrgPayload<InviteUsersToOrganizationResult>(res)
}

export const acceptOrgInvite = async (
  state: State,
  req: { inviteToken: string }
): Promise<ApiResponse<{ organizationId?: string; role?: string } | undefined>> => {
  const cf = await loadCloudFunctionsHandler()
  const res = await cf.acceptOrgInvite(req, state)
  return unwrapOrgPayload(res)
}

/**
 * Unauthenticated invite lookup — returns invitee email + invite/org status so the
 * accept page can redirect anonymous visitors to /login with email prefilled and locked.
 *
 * Backend returns a uniform empty stub (email = '', status = EXPIRED) for unknown or
 * non-pending tokens — the FE treats empty email as "invalid or expired" without leaking
 * which tokens exist.
 */
export const getInviteEmailByToken = async (req: {
  inviteToken: string
}): Promise<ApiResponse<{ email: string; status: string; isOnboarding: boolean; organizationName: string }>> => {
  const cf = await loadCloudFunctionsHandler()
  return cf.getInviteEmailByToken(req)
}

export const refreshAuthClaimsForLoggedInUser = async (state: State): Promise<ApiResponse<unknown>> => {
  const cf = await loadCloudFunctionsHandler()
  return cf.setCustomUserClaimsForLoggedInUser(state)
}

export type CompleteOrganizationOnboardingPayload = {
  name: string
  address: {
    street: string
    city: string
    country: string
    postalCode?: string
  }
  logo?: string
}

/**
 * Finalize organization onboarding. The wizard at `/org/:orgId/onboarding` is the
 * primary caller — submits the org name, address (postalCode optional), and an
 * optional logo URL. On success the backend flips the org `status` from
 * `PENDING_ONBOARDING` to `ACTIVE`.
 */
export const completeOrganizationOnboarding = async (
  state: State,
  req: { organizationId: string; payload: CompleteOrganizationOnboardingPayload }
): Promise<ApiResponse<{ success: true } | undefined>> => {
  const cf = await loadCloudFunctionsHandler()
  const res = await cf.completeOrganizationOnboarding(req, state)
  if (!res.success) {
    return { data: undefined, success: false, error: res.error }
  }
  if (!res.data?.success) {
    return { data: undefined, success: false, error: res.error }
  }
  return { data: { success: true }, success: true }
}
